/**
 * SecureMessagingWidget — Direct messaging for subject dashboard (DRR-321)
 *
 * Shows recent notifications related to this subject.
 * Uses the notification system as messaging isn't yet implemented.
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Card,
  Skeleton,
  Alert,
  Box,
  ScrollArea,
  ThemeIcon,
} from '@mantine/core';
import {
  IconMail,
  IconAlertCircle,
  IconMailOpened,
  IconClock,
  IconChevronRight,
  IconBell,
} from '@tabler/icons-react';
import {
  useListNotificationsForRecipientQuery,
  GqlNotificationStatus,
  GqlNotificationPriority,
} from '@/generated/graphql';
import type { WidgetProps } from '@ferp/core';

const PRIORITY_COLOR: Record<string, string> = {
  [GqlNotificationPriority.CRITICAL]: 'red',
  [GqlNotificationPriority.HIGH]: 'orange',
  [GqlNotificationPriority.MEDIUM]: 'blue',
  [GqlNotificationPriority.LOW]: 'gray',
  [GqlNotificationPriority.INFO]: 'gray',
};

export function SecureMessagingWidget({ subjectActorId }: WidgetProps) {
  const { data, loading, error } = useListNotificationsForRecipientQuery({
    variables: { recipientActorId: subjectActorId ?? '', limit: 10 },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const notifications = useMemo(() => {
    return data?.listNotificationsForRecipient?.items ?? [];
  }, [data]);

  const unreadCount = notifications.filter(
    n => n.status === GqlNotificationStatus.PENDING || n.status === GqlNotificationStatus.SENT
  ).length;

  if (loading && notifications.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Error loading notifications.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <IconBell size={16} color="var(--mantine-color-blue-6)" />
          <Text fw={600} size="sm">Notifications</Text>
          {unreadCount > 0 && (
            <Badge size="sm" color="red">{unreadCount} new</Badge>
          )}
        </Group>
      </Group>

      {notifications.length === 0 ? (
        <Alert icon={<IconMail size={16} />} color="blue" variant="light">
          No notifications for this subject.
        </Alert>
      ) : (
        <ScrollArea.Autosize mah={200}>
          <Stack gap="xs">
            {notifications.map((notif) => {
              const isRead = notif.status === GqlNotificationStatus.READ;
              return (
                <Card
                  key={notif.id}
                  withBorder
                  padding="xs"
                  radius="sm"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: isRead ? undefined : 'var(--mantine-color-blue-0)',
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                      <ThemeIcon size="sm" variant="light" color={isRead ? 'gray' : 'blue'}>
                        {isRead ? <IconMailOpened size={14} /> : <IconMail size={14} />}
                      </ThemeIcon>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text fw={isRead ? 400 : 600} size="sm" lineClamp={1}>
                          {notif.notificationTitle}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {notif.messageTemplate}
                        </Text>
                      </Box>
                    </Group>
                    <Group gap={4}>
                      {notif.priority === GqlNotificationPriority.CRITICAL && (
                        <Badge size="xs" color="red">!</Badge>
                      )}
                      {notif.sentAt && (
                        <Group gap={2}>
                          <IconClock size={10} color="gray" />
                          <Text size="xs" c="dimmed">
                            {new Date(notif.sentAt).toLocaleDateString()}
                          </Text>
                        </Group>
                      )}
                      <IconChevronRight size={14} color="gray" />
                    </Group>
                  </Group>
                </Card>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      )}
    </Stack>
  );
}
